import {RefObject, useCallback} from "react";

import {useIntersection} from "./use-intersection";

export interface UseVideoIntersectionOptions {
    /**
     * Container element to search for videos
     * Can be a ref or a function that returns the container element
     * If not provided, searches in the observed element
     */
    containerRef?: RefObject<HTMLElement | null> | (() => HTMLElement | null);

    /**
     * Threshold for intersection ratio
     * @default 0.1
     */
    threshold?: number;

    /**
     * Whether to pause videos when leaving viewport
     * @default true
     */
    pauseOnLeave?: boolean;

    /**
     * Whether to mute videos when leaving viewport
     * @default true
     */
    muteOnLeave?: boolean;
}

/**
 * Hook for pausing/muting videos when element leaves viewport
 *
 * @example
 * ```tsx
 * const sectionRef = useRef<HTMLElement>(null);
 * const sliderRef = useRef<HTMLDivElement>(null);
 * useVideoIntersection(sectionRef, {
 *   containerRef: sliderRef,
 *   pauseOnLeave: true,
 *   muteOnLeave: true,
 * });
 * ```
 */
export function useVideoIntersection(elementRef: RefObject<HTMLElement | null>, options: UseVideoIntersectionOptions = {}): void {
    const {containerRef, threshold = 0.1, pauseOnLeave = true, muteOnLeave = true} = options;

    const handleLeave = useCallback(() => {
        let container: HTMLElement | null = null;

        if (containerRef) {
            if (typeof containerRef === "function") {
                container = containerRef();
            } else {
                container = containerRef.current;
            }
        }

        if (!container) {
            container = elementRef.current;
        }

        if (!container) {
            return;
        }

        const videoElements = container.querySelectorAll<HTMLVideoElement>("video");
        videoElements.forEach((video) => {
            if (pauseOnLeave) {
                video.pause();
            }
            if (muteOnLeave) {
                video.muted = true;
            }
        });
    }, [containerRef, elementRef, pauseOnLeave, muteOnLeave]);

    useIntersection(
        elementRef,
        {
            onLeave: handleLeave,
        },
        {
            threshold,
        },
    );
}
